/**
 * Who each week's remittance belongs to, for the period on the Finance page.
 *
 * Every row is one rider's charged week; every total is the sum of the rows
 * shown, and the cash figures are by payment date — the basis company revenue
 * is counted on. Filter by owner, investor, rider, bike and state; the period
 * (day, week, month, year or custom) is the page's own.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { BIKE_OWNERSHIPS, BIKE_OWNERSHIP_LABELS, statesOf, type BikeOwnership } from '@cenox/shared';
import { Card, ErrorState, Select, TableSkeleton } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { useRiders } from '../logistics/data';
import { useBikes } from '../riderFinance/data';
import { useInvestorOptions, useRemittanceDistribution } from '../riderFinance/investorData';

const STATUS_TEXT: Record<string, string> = {
  paid: 'Paid',
  part_paid: 'Partially paid',
  unpaid: 'Unpaid',
  waived: 'Waived',
};

export function RemittanceDistributionReport({ from, to }: { from: string; to: string }): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const [ownership, setOwnership] = useState<BikeOwnership | ''>('');
  const [investorId, setInvestorId] = useState('');
  const [riderId, setRiderId] = useState('');
  const [bikeId, setBikeId] = useState('');
  const [state, setState] = useState('');

  const investors = useInvestorOptions();
  const riders = useRiders();
  const bikes = useBikes();
  const states = statesOf(business?.countryCode ?? 'NG');

  const query = useMemo(
    () => ({
      from,
      to,
      ...(ownership ? { ownership } : {}),
      ...(investorId ? { investorId } : {}),
      ...(riderId ? { riderId } : {}),
      ...(bikeId ? { bikeId } : {}),
      ...(state ? { state } : {}),
    }),
    [from, to, ownership, investorId, riderId, bikeId, state],
  );
  const report = useRemittanceDistribution(query);
  const r = report.data;

  return (
    <Card>
      <h2 className="text-sm font-semibold text-slate-900">Remittance by owner</h2>
      <p className="mb-3 mt-0.5 text-xs text-slate-500">
        Each rider&apos;s weeks ending {from} → {to}: what was due, collected and outstanding, and how much of it is
        the investor&apos;s and how much the company&apos;s.
      </p>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            label="Owner"
            value={ownership}
            onChange={(e) => {
              setOwnership(e.target.value as BikeOwnership | '');
              setInvestorId('');
            }}
            options={[{ value: '', label: 'All owners' }, ...BIKE_OWNERSHIPS.map((o) => ({ value: o, label: BIKE_OWNERSHIP_LABELS[o] }))]}
          />
        </div>
        <div className="w-44">
          <Select
            label="Investor"
            value={investorId}
            onChange={(e) => {
              setInvestorId(e.target.value);
              if (e.target.value) setOwnership('investor');
            }}
            options={[{ value: '', label: 'All investors' }, ...(investors.data ?? []).map((i) => ({ value: i.id, label: i.name }))]}
          />
        </div>
        <div className="w-44">
          <Select
            label="Rider"
            value={riderId}
            onChange={(e) => setRiderId(e.target.value)}
            options={[{ value: '', label: 'All riders' }, ...(riders.data?.data ?? []).map((x) => ({ value: x.id, label: x.canonicalName }))]}
          />
        </div>
        <div className="w-40">
          <Select
            label="Bike"
            value={bikeId}
            onChange={(e) => setBikeId(e.target.value)}
            options={[{ value: '', label: 'All bikes' }, ...(bikes.data?.bikes ?? []).map((b) => ({ value: b.id, label: b.registration }))]}
          />
        </div>
        {states.length > 0 ? (
          <div className="w-40">
            <Select
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              options={[{ value: '', label: 'All states' }, ...states.map((s) => ({ value: s.code, label: s.name }))]}
            />
          </div>
        ) : null}
      </div>

      {report.isError ? (
        <ErrorState message="Could not load the remittance report." onRetry={() => void report.refetch()} />
      ) : !r ? (
        <TableSkeleton rows={3} columns={6} />
      ) : (
        <>
          <dl className="mb-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
            {(
              [
                ['Gross collected', r.cash.grossCollected, 'By payment date'],
                ['Company revenue', r.cash.companyRevenue, 'Company share received'],
                ['Investor funds collected', r.cash.investorCollected, 'Owed to investors'],
                ['Paid to investors', r.cash.investorPaidOut, 'In these dates'],
                ['Owed to investors', r.cash.investorPayable, `At ${to}`],
              ] as const
            ).map(([label, value, hint]) => (
              <div key={label} className="rounded-lg border border-slate-200 p-2.5">
                <dt className="text-xs text-slate-500">{label}</dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-slate-900">{money(value, currency)}</dd>
                <dd className="text-[11px] text-slate-400">{hint}</dd>
              </div>
            ))}
          </dl>

          <div className="-mx-3 overflow-x-auto px-3">
            <table className="w-full min-w-[1100px] text-sm" aria-label="Remittance by owner">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Week</th>
                  <th className="py-2 pr-3 font-medium">Rider · bike</th>
                  <th className="py-2 pr-3 font-medium">Owner</th>
                  <th className="py-2 pr-3 text-right font-medium">Due</th>
                  <th className="py-2 pr-3 text-right font-medium">Collected</th>
                  <th className="py-2 pr-3 text-right font-medium">Outstanding</th>
                  <th className="py-2 pr-3 text-right font-medium">Investor due</th>
                  <th className="py-2 pr-3 text-right font-medium">Investor collected</th>
                  <th className="py-2 pr-3 text-right font-medium">Investor outstanding</th>
                  <th className="py-2 pr-3 text-right font-medium">Company due</th>
                  <th className="py-2 pr-3 text-right font-medium">Company collected</th>
                  <th className="py-2 pr-3 text-right font-medium">Company outstanding</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {r.rows.map((w) => (
                  <tr key={w.remittanceId}>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-xs text-slate-500">
                      {w.weekStartsOn}
                      <span className="block">to {w.weekEndsOn}</span>
                    </td>
                    <td className="py-1.5 pr-3">
                      {w.riderName}
                      <span className="block text-xs text-slate-400">
                        {w.bikeRegistration ?? 'No vehicle'}
                        {w.stateCode ? ` · ${w.stateCode}` : ''}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-xs">
                      {w.ownership ? BIKE_OWNERSHIP_LABELS[w.ownership] : '—'}
                      {w.investorName ? <span className="block text-slate-500">{w.investorName}</span> : null}
                      {w.legacy && w.ownership === 'investor' ? (
                        <span className="block text-slate-400">before shares: all company</span>
                      ) : null}
                    </td>
                    {(
                      [
                        w.amountDue,
                        w.collected,
                        w.outstanding,
                        w.investorEntitlement,
                        w.investorCollected,
                        w.investorOutstanding,
                        w.companyEntitlement,
                        w.companyCollected,
                        w.companyOutstanding,
                      ] as const
                    ).map((v, i) => (
                      <td key={i} className="py-1.5 pr-3 text-right tabular-nums">
                        {money(v, currency)}
                      </td>
                    ))}
                    <td className="py-1.5 text-xs">{STATUS_TEXT[w.status] ?? w.status}</td>
                  </tr>
                ))}
              </tbody>
              {r.rows.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-slate-300 font-semibold">
                    <td className="py-2 pr-3" colSpan={3}>
                      Total · {r.rows.length} {r.rows.length === 1 ? 'week' : 'weeks'}
                    </td>
                    {(
                      [
                        r.totals.amountDue,
                        r.totals.collected,
                        r.totals.outstanding,
                        r.totals.investorEntitlement,
                        r.totals.investorCollected,
                        r.totals.investorOutstanding,
                        r.totals.companyEntitlement,
                        r.totals.companyCollected,
                        r.totals.companyOutstanding,
                      ] as const
                    ).map((v, i) => (
                      <td key={i} className="py-2 pr-3 text-right tabular-nums">
                        {money(v, currency)}
                      </td>
                    ))}
                    <td />
                  </tr>
                </tfoot>
              ) : null}
            </table>
            {r.rows.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No charged weeks match.</p>
            ) : null}
          </div>
        </>
      )}
    </Card>
  );
}
